<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<script setup>
import { ref, computed, onUnmounted } from 'vue';

// Reconnect banner + QR modal for Evolution API inboxes whose WhatsApp
// pairing has been lost (state != 'open'). Mounted on the inbox Settings
// page next to the other channel reauthorize banners. Reuses the same
// /evolution/qrcode and /evolution/state endpoints as the create-inbox
// flow, but keyed off the existing inbox's webhook_url (which embeds the
// Evolution instance name) instead of asking the user to retype anything.

const props = defineProps({
  inbox: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});

const wootApi = window.axios;

const showModal = ref(false);
const qrSource = ref('');
const stateLabel = ref('');
const loading = ref(false);
const errorMessage = ref('');
const expiresIn = ref(0);
// Set to true the moment the QR is scanned and the modal auto-closes, so
// we can surface a non-intrusive notice on the inbox settings page warning
// the agent that messages received while the inbox was offline may NOT have
// been forwarded to Chatwoot (WhatsApp does not redeliver to a re-paired
// linked device, and Evolution is configured with importMessages: false).
const justReconnected = ref(false);

let pollTimer = null;
let qrTimer = null;

const STATE_POLL_INTERVAL_MS = 4000;
const QR_REFRESH_INTERVAL_MS = 50000;

// Evolution instance name is the last segment of the channel webhook_url,
// e.g. http://evolution:8080/chatwoot/webhook/acc1-crow-wpp -> acc1-crow-wpp.
const instanceName = computed(() => {
  const url = props.inbox?.webhook_url || '';
  if (!url) return '';
  const parts = url.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
});

const basePath = computed(
  () => `/api/v1/accounts/${props.accountId}/integrations/evolution`
);

const qrImageSrc = computed(() => {
  if (!qrSource.value) return '';
  if (qrSource.value.startsWith('data:')) return qrSource.value;
  return `data:image/png;base64,${qrSource.value}`;
});

const stopTimers = () => {
  if (pollTimer) clearInterval(pollTimer);
  if (qrTimer) clearInterval(qrTimer);
  pollTimer = null;
  qrTimer = null;
  expiresIn.value = 0;
};

const fetchQr = async ({ force = false } = {}) => {
  try {
    const { data } = await wootApi.get(`${basePath.value}/qrcode`, {
      params: { instance_name: instanceName.value, force },
    });
    const next = data?.base64 || data?.code || data?.qrcode?.base64 || '';
    if (next) {
      qrSource.value = next;
      expiresIn.value = Math.ceil(QR_REFRESH_INTERVAL_MS / 1000);
    }
  } catch (_) {
    // ignore — keep last QR until next tick
  }
};

const pollState = async () => {
  try {
    const { data } = await wootApi.get(`${basePath.value}/state`, {
      params: { instance_name: instanceName.value },
    });
    const s = data?.instance?.state || data?.state || '';
    if (s) stateLabel.value = s;
    if (s === 'open') {
      stopTimers();
      // Mark inbox locally so the red dot disappears without waiting for the
      // 2-min cron poll. The next cron tick will reconfirm.
      if (props.inbox.additional_attributes) {
        props.inbox.additional_attributes.connection_state = 'open';
        props.inbox.additional_attributes.whatsapp_disconnected = false;
      }
      showModal.value = false;
      justReconnected.value = true;
    }
  } catch (_) {
    // keep polling
  }
};

const openReconnect = async () => {
  if (!instanceName.value) {
    errorMessage.value =
      'Não foi possível identificar a instância Evolution desta caixa.';
    showModal.value = true;
    return;
  }
  errorMessage.value = '';
  showModal.value = true;
  loading.value = true;
  qrSource.value = '';
  try {
    // force=true triggers /instance/restart in the backend if the instance
    // is not already in `open` state, kicking Baileys back to life and
    // yielding a brand new QR. Only do this once, on open: subsequent refreshes
    // must NOT restart again (that would tear down the pairing the user is
    // mid-scan on) — a plain connect returns the latest rotating QR.
    await fetchQr({ force: true });
    stopTimers();
    pollTimer = setInterval(pollState, STATE_POLL_INTERVAL_MS);
    qrTimer = setInterval(async () => {
      expiresIn.value = Math.max(expiresIn.value - 1, 0);
      if (expiresIn.value <= 2) {
        await fetchQr();
      }
    }, 1000);
  } catch (e) {
    errorMessage.value = e?.message || 'Erro ao gerar QR Code.';
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  stopTimers();
  showModal.value = false;
  qrSource.value = '';
  stateLabel.value = '';
};

onUnmounted(stopTimers);
</script>

<template>
  <div>
    <div
      v-if="!justReconnected"
      class="flex items-center justify-between gap-3 p-4 rounded-md border border-n-ruby-7 bg-n-ruby-3"
    >
      <div class="flex-1">
        <div class="text-sm font-medium text-n-ruby-12">
          WhatsApp desconectado
        </div>
        <div class="text-xs text-n-ruby-11">
          A conexão com o WhatsApp foi perdida. Clique em Reconectar para
          escanear um novo QR Code sem precisar recriar a caixa de entrada.
        </div>
      </div>
      <button
        class="px-3 py-1.5 text-xs font-medium rounded bg-n-brand text-n-solid-3"
        @click="openReconnect"
      >
        Reconectar
      </button>
    </div>

    <div
      v-if="justReconnected"
      class="flex items-start justify-between gap-3 p-4 rounded-md border border-n-amber-7 bg-n-amber-3"
    >
      <div class="flex-1">
        <div class="text-sm font-medium text-n-amber-12">
          Conexão restabelecida
        </div>
        <div class="text-xs text-n-amber-11">
          Mensagens recebidas no celular enquanto a caixa estava desconectada
          podem não ter sido sincronizadas para o CROWORK. Verifique o
          WhatsApp no celular para garantir que nenhuma conversa importante
          ficou de fora deste período.
        </div>
      </div>
      <button
        class="px-2 py-1 text-xs rounded border border-n-amber-7 text-n-amber-12"
        @click="justReconnected = false"
      >
        Entendi
      </button>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      @click.self="closeModal"
    >
      <div
        class="bg-n-solid-1 rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
      >
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-base font-semibold text-n-slate-12">
            Reconectar WhatsApp
          </h2>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="closeModal"
          >
            ✕
          </button>
        </div>

        <p v-if="errorMessage" class="mb-3 text-sm text-red-500">
          {{ errorMessage }}
        </p>

        <div
          class="flex justify-center items-center w-full aspect-square bg-white rounded border border-n-weak"
        >
          <img
            v-if="qrImageSrc"
            :src="qrImageSrc"
            alt="QR Code reconexão"
            class="w-full h-full object-contain"
          />
          <span v-else class="text-sm text-n-slate-11">
            {{ loading ? 'Gerando QR…' : 'Aguardando QR…' }}
          </span>
        </div>

        <p class="mt-3 text-xs text-n-slate-11">
          Abra o WhatsApp → Aparelhos conectados → Conectar um aparelho.
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          Estado: <strong class="text-n-slate-12">{{ stateLabel || '—' }}</strong>
          <span v-if="expiresIn"> · QR renova em {{ expiresIn }}s</span>
        </p>

        <div class="flex justify-end mt-4">
          <button
            class="px-3 py-1.5 text-xs rounded border border-n-weak text-n-slate-11"
            @click="closeModal"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
